import { Typography } from "antd";
import { Package, Truck } from "lucide-react";
import { FC } from "react";
import { TServiceTicket } from "~/types";
import { calculateCubicFeetInventory, capitalize } from "~/utils";

const { Text } = Typography;

interface ContainerInfoSummaryProps {
  serviceTickets: TServiceTicket[];
  variant?: "inline" | "block";
  className?: string;
}

export const ContainerInfoSummary: FC<ContainerInfoSummaryProps> = ({
  serviceTickets,
  variant = "inline",
  className = "",
}) => {
  if (!serviceTickets || serviceTickets.length === 0) {
    return (
      <Text type="secondary" className={className}>
        No container information
      </Text>
    );
  }

  // Aggregate container information
  const totalPackages = serviceTickets.reduce(
    (total, ticket) => total + (ticket.inventories?.length || 0),
    0
  );

  const totalVolume = serviceTickets.reduce((total, ticket) => {
    const stats = ticket.inventories
      ? calculateCubicFeetInventory(ticket.inventories)
      : { totalCubicFeet: 0 };
    return total + stats.totalCubicFeet;
  }, 0);

  const totalWeight = serviceTickets.reduce((total, ticket) => {
    const stats = ticket.inventories
      ? calculateCubicFeetInventory(ticket.inventories)
      : { totalWeight: 0 };
    return total + (stats.totalWeight || 0);
  }, 0);

  const serviceTypes = [
    ...new Set(serviceTickets.map((ticket) => ticket.serviceType)),
  ];
  const serviceTypeLabels = serviceTypes
    .map((type) => capitalize(type))
    .join(", ");

  if (variant === "inline") {
    return (
      <div className={`flex items-center space-x-4 text-sm ${className}`}>
        <div className="flex items-center space-x-1">
          <Truck size={14} className="text-gray-400" />
          <Text type="secondary">Services:</Text>
          <Text>{serviceTypeLabels}</Text>
        </div>
        <div className="flex items-center space-x-1">
          <Package size={14} className="text-gray-400" />
          <Text>
            <span className="font-medium">{totalPackages}</span>
            <span className="text-gray-500"> packages</span>
          </Text>
        </div>
        <div>
          <Text>
            <span className="font-medium">{totalVolume}</span>
            <span className="text-gray-500"> ft³</span>
          </Text>
        </div>
        {totalWeight > 0 && (
          <div>
            <Text>
              <span className="font-medium">{totalWeight}</span>
              <span className="text-gray-500"> lbs</span>
            </Text>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center space-x-2">
        <Truck size={16} className="text-gray-400" />
        <Text strong>Container Summary</Text>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-500">Services</div>
          <div className="font-medium">{serviceTypeLabels}</div>
        </div>
        <div>
          <div className="text-gray-500">Total Packages</div>
          <div className="font-medium">{totalPackages} items</div>
        </div>
        <div>
          <div className="text-gray-500">Total Volume</div>
          <div className="font-medium">{totalVolume} ft³</div>
        </div>
        {totalWeight > 0 && (
          <div>
            <div className="text-gray-500">Total Weight</div>
            <div className="font-medium">{totalWeight} lbs</div>
          </div>
        )}
      </div>
    </div>
  );
};
